import classNames from 'classnames'
import { type FC, type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react'
import styles from './EmailEditor.module.scss'
import { type EmailVariable } from './emailTemplateTypes'
import { matchVariables, orderVariables, variableOptionId } from './variableGroups'
import { VariableMenu } from './VariableMenu'

export interface InsertVariableMenuProps {
  variables: EmailVariable[]
  onInsert: (variable: EmailVariable) => void
}

export const InsertVariableMenu: FC<InsertVariableMenuProps> = ({ variables, onInsert }) => {
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()
  const ordered = useMemo(() => orderVariables(matchVariables(variables, query)), [variables, query])

  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setHighlighted((current) => (ordered.length ? (current + step + ordered.length) % ordered.length : 0))
      return
    }
    if (event.key === 'Enter' && ordered[highlighted]) {
      event.preventDefault()
      onInsert(ordered[highlighted])
    }
  }

  return (
    <VariableMenu
      variables={ordered}
      highlighted={highlighted}
      onHighlight={setHighlighted}
      onPick={onInsert}
      listboxId={listboxId}
      header={
        <div className='p-2 pb-1'>
          <div className='d-flex align-items-center gap-2 px-2 border rounded-2 zocks-border-color-primary'>
            <i className='bi bi-search zocks-text-gray-500' aria-hidden='true' />
            <input
              ref={searchRef}
              className={classNames(styles.variableSearch, 'flex-grow-1 border-0 paragraph-medium fw-normal bg-transparent')}
              placeholder='Search variables'
              aria-label='Search variables'
              role='combobox'
              aria-expanded
              aria-controls={listboxId}
              aria-autocomplete='list'
              aria-activedescendant={ordered[highlighted] ? variableOptionId(listboxId, highlighted) : undefined}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setHighlighted(0)
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>
      }
    />
  )
}
